import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import DatePicker from "react-datepicker";
import moment from "moment";
import { toast } from "react-toastify";

import AppLayout from "layout/AppLayout";
import axios from "axiosClient";
import { currencyFormatter } from "utils/formatter";

import "react-datepicker/dist/react-datepicker.css";

const toDot = (date) => moment(date).format("DD.MM.YYYY");

const money = (value) => {
  const text = `${value ?? ""}`.trim();
  if (!text) return "-";
  return currencyFormatter(text, "UZS");
};

/** Why statements were left out, in the order they are worth explaining. */
const SKIP_REASONS = [
  ["alreadyHandled", "Already applied or imported"],
  ["alreadyLinked", "Payment already carries this BXA number"],
  ["refund", "Money out (refund)"],
  ["noContract", "No contract on the statement"],
  ["contractNotFetched", "Contract not in this fetch"],
  ["notBxa", "Contract is not a BXA number"],
  ["noExamDate", "Contract has no exam date"],
  ["noPerson", "No candidate on the statement"],
  ["personNotFetched", "Candidate not in this fetch"],
  ["noPinpp", "Candidate has no pinpp"],
  ["badAmount", "Amount could not be read"],
];

const Stat = ({ label, value, color }) => (
  <Col md="3" sm="6" className="mb-3">
    <Card className="shadow-sm h-100" style={{ borderLeft: `4px solid ${color}` }}>
      <CardBody>
        <div className="text-muted small">{label}</div>
        <h3 className="mb-0">{value ?? 0}</h3>
      </CardBody>
    </Card>
  </Col>
);

const SmartupSync = () => {
  // Yesterday by default: today's records are still being entered in Smartup.
  const yesterday = () => moment().subtract(1, "day").toDate();
  const [begin, setBegin] = useState(yesterday);
  const [end, setEnd] = useState(yesterday);
  const [fetching, setFetching] = useState(false);
  const [history, setHistory] = useState([]);
  const [review, setReview] = useState(null);
  const [loadingReview, setLoadingReview] = useState(false);
  const [matchPicks, setMatchPicks] = useState([]);
  const [importPicks, setImportPicks] = useState([]);
  const [applying, setApplying] = useState(false);
  const [importing, setImporting] = useState(false);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await axios.get("/api/smartup/fetches");
      setHistory(data?.items || []);
    } catch {
      /* the history is a convenience; the page works without it */
    }
  }, []);

  const loadReview = useCallback(async (fetchId) => {
    setLoadingReview(true);
    setMatchPicks([]);
    setImportPicks([]);
    try {
      const { data } = await axios.get("/api/smartup/reconcile", {
        params: fetchId ? { fetchId } : {},
      });
      setReview(data);
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Could not load the reconciliation",
      );
    } finally {
      setLoadingReview(false);
    }
  }, []);

  // Only the list of windows fetched before. The review is worked out after a
  // fetch, or when one of those windows is picked, so opening the page is idle.
  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleFetch = async () => {
    setFetching(true);
    try {
      const { data } = await axios.post("/api/smartup/fetch", {
        begin: toDot(begin),
        end: toDot(end),
      });
      toast.success(
        `Fetched ${data?.bankOperations?.received ?? 0} statements, ${
          data?.contracts?.received ?? 0
        } contracts, ${data?.people?.received ?? 0} people`,
      );
      await loadHistory();
      await loadReview(data?.fetchId);
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "The Smartup fetch failed",
      );
    } finally {
      setFetching(false);
    }
  };

  const matched = review?.items?.matched || [];
  const unmatched = review?.items?.unmatched || [];
  const ambiguous = review?.items?.ambiguous || [];
  const counts = review?.counts || {};

  const togglePick = (list, setList, id) =>
    setList(
      list.includes(id) ? list.filter((one) => one !== id) : [...list, id],
    );

  const allMatchIds = useMemo(
    () => matched.map((row) => row.operationId),
    [matched],
  );
  const allImportIds = useMemo(
    () => unmatched.map((row) => row.operationId),
    [unmatched],
  );

  const handleApply = async () => {
    const items = matched
      .filter((row) => matchPicks.includes(row.operationId))
      .map((row) => ({
        operationId: row.operationId,
        paymentId: row.payment.id,
      }));
    if (!items.length) return;

    setApplying(true);
    try {
      const { data } = await axios.post("/api/smartup/apply", { items });
      const { applied = 0, skipped = 0 } = data?.summary || {};
      if (applied) toast.success(`Replaced ${applied} enquiry ID(s)`);
      if (skipped) toast.warn(`${skipped} were left alone — see the table`);
      await loadReview(review?.fetch?.id);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not apply");
    } finally {
      setApplying(false);
    }
  };

  const handleImport = async () => {
    const operationIds = unmatched
      .filter((row) => importPicks.includes(row.operationId))
      .map((row) => row.operationId);
    if (!operationIds.length) return;

    setImporting(true);
    try {
      const { data } = await axios.post("/api/smartup/import", {
        operationIds,
      });
      const { imported = 0, skipped = 0 } = data?.summary || {};
      if (imported) toast.success(`Imported ${imported} payment(s)`);
      if (skipped) {
        const reasons = [
          ...new Set((data?.skipped || []).map((s) => s.reason).filter(Boolean)),
        ];
        toast.warn(
          `${skipped} were skipped${reasons.length ? `: ${reasons.join("; ")}` : ""}`,
        );
      }
      await loadReview(review?.fetch?.id);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not import");
    } finally {
      setImporting(false);
    }
  };

  return (
    <AppLayout>
      <div className="dashboard-wrapper">
        <Card className="m-4 shadow">
          <CardBody>
            <CardTitle tag="h3" className="mb-1">
              Smartup Reconciliation
            </CardTitle>
            <p className="text-muted">
              Pull a window of Smartup records, then replace the enquiry ID of
              matching IELTS payments with the BXA contract number. Statements
              with a BXA contract and no payment of ours can be imported as new
              payments.
            </p>
            <Alert color="info" className="text-small">
              The date range is <strong>when a record was entered in
              Smartup</strong> — the export offers no other filter — so a single
              day can hold operation dates weeks apart. Smartup allows roughly
              300 statement calls and 100 contract and person calls a day, so
              fetched records are stored here and reviewed without fetching
              again.
            </Alert>

            <Row className="text-left align-items-end">
              <Col md="3">
                <FormGroup>
                  <Label className="font-weight-bold">Entered from</Label>
                  <DatePicker
                    selected={begin}
                    onChange={(date) => setBegin(date)}
                    dateFormat="dd.MM.yyyy"
                    className="form-control"
                    disabled={fetching}
                  />
                </FormGroup>
              </Col>
              <Col md="3">
                <FormGroup>
                  <Label className="font-weight-bold">Entered to</Label>
                  <DatePicker
                    selected={end}
                    onChange={(date) => setEnd(date)}
                    dateFormat="dd.MM.yyyy"
                    className="form-control"
                    disabled={fetching}
                  />
                </FormGroup>
              </Col>
              <Col md="6" className="pb-3">
                <Button color="primary" onClick={handleFetch} disabled={fetching}>
                  {fetching ? (
                    <>
                      <Spinner size="sm" className="mr-2" />
                      Fetching from Smartup…
                    </>
                  ) : (
                    "Fetch from Smartup"
                  )}
                </Button>
                <Button
                  color="light"
                  className="ml-2"
                  onClick={() => loadReview(review?.fetch?.id)}
                  disabled={loadingReview}
                >
                  Refresh review
                </Button>
              </Col>
            </Row>

            {history.length > 0 && (
              <div className="mb-4">
                <h6>Already fetched</h6>
                <div style={{ maxHeight: 130, overflow: "auto" }}>
                  {history.map((one) => (
                    <Badge
                      key={one._id}
                      color={
                        one.status === "done"
                          ? review?.fetch?.id === one._id
                            ? "primary"
                            : "light"
                          : "danger"
                      }
                      className="mr-2 mb-2 p-2"
                      style={{ cursor: "pointer" }}
                      onClick={() => loadReview(one._id)}
                    >
                      {one.begin} – {one.end} ({one.bankOperations?.received ?? 0})
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {loadingReview && (
              <div className="d-flex align-items-center my-3">
                <Spinner size="sm" className="mr-2" />
                <span>Working out the matches…</span>
              </div>
            )}

            {!loadingReview && !review?.fetch && (
              <Alert color="secondary">
                Pick a range and fetch from Smartup, or open one of the ranges
                already fetched above.
              </Alert>
            )}

            {!loadingReview && review?.fetch && (
              <>
                <Row className="mt-3">
                  <Stat label="Statements" value={counts.operations} color="#007bff" />
                  <Stat label="Ready to replace" value={counts.matched} color="#28a745" />
                  <Stat label="No payment of ours" value={counts.unmatched} color="#ffc107" />
                  <Stat label="Ambiguous" value={counts.ambiguous} color="#dc3545" />
                </Row>

                <h5 className="mt-4">
                  Replace enquiry ID{" "}
                  <Badge color="success" className="text-white">
                    {matched.length}
                  </Badge>
                </h5>
                {matched.length === 0 ? (
                  <p className="text-muted">Nothing to replace in this fetch.</p>
                ) : (
                  <>
                    <div className="mb-2">
                      <Button
                        color="light"
                        size="sm"
                        onClick={() =>
                          setMatchPicks(
                            matchPicks.length === allMatchIds.length
                              ? []
                              : allMatchIds,
                          )
                        }
                      >
                        {matchPicks.length === allMatchIds.length
                          ? "Clear all"
                          : "Select all"}
                      </Button>
                      <Button
                        color="primary"
                        size="sm"
                        className="ml-2"
                        disabled={!matchPicks.length || applying}
                        onClick={handleApply}
                      >
                        {applying
                          ? "Applying…"
                          : `Apply ${matchPicks.length} selected`}
                      </Button>
                    </div>
                    <div style={{ maxHeight: 420, overflow: "auto" }}>
                      <Table striped size="sm" className="mb-0">
                        <thead>
                          <tr>
                            <th style={{ width: 34 }} />
                            <th>Paid</th>
                            <th>Candidate</th>
                            <th>PINPP</th>
                            <th>Exam date</th>
                            <th>Amount</th>
                            <th>Current enquiry ID</th>
                            <th>New (BXA)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {matched.map((row) => (
                            <tr key={row.operationId}>
                              <td>
                                <Input
                                  type="checkbox"
                                  className="position-static m-0"
                                  checked={matchPicks.includes(row.operationId)}
                                  onChange={() =>
                                    togglePick(
                                      matchPicks,
                                      setMatchPicks,
                                      row.operationId,
                                    )
                                  }
                                />
                              </td>
                              <td>{row.operationDate}</td>
                              <td className="text-truncate" style={{ maxWidth: 220 }}>
                                {row.candidateName || row.payment.candidateName}
                              </td>
                              <td>{row.pinpp}</td>
                              <td>{row.examDate}</td>
                              <td>{money(row.amount)}</td>
                              <td>
                                <code>{row.payment.enquiryId || "—"}</code>
                              </td>
                              <td>
                                <code>{row.contractNumber}</code>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </>
                )}

                <h5 className="mt-4">
                  No payment of ours{" "}
                  <Badge color="warning">{unmatched.length}</Badge>
                </h5>
                {unmatched.length === 0 ? (
                  <p className="text-muted">
                    Every BXA statement in this fetch has a payment.
                  </p>
                ) : (
                  <>
                    <div className="mb-2">
                      <Button
                        color="light"
                        size="sm"
                        onClick={() =>
                          setImportPicks(
                            importPicks.length === allImportIds.length
                              ? []
                              : allImportIds,
                          )
                        }
                      >
                        {importPicks.length === allImportIds.length
                          ? "Clear all"
                          : "Select all"}
                      </Button>
                      <Button
                        color="warning"
                        size="sm"
                        className="ml-2"
                        disabled={!importPicks.length || importing}
                        onClick={handleImport}
                      >
                        {importing
                          ? "Importing…"
                          : `Import ${importPicks.length} as payments`}
                      </Button>
                    </div>
                    <div style={{ maxHeight: 420, overflow: "auto" }}>
                      <Table striped size="sm" className="mb-0">
                        <thead>
                          <tr>
                            <th style={{ width: 34 }} />
                            <th>Paid</th>
                            <th>Candidate</th>
                            <th>PINPP</th>
                            <th>Exam date</th>
                            <th>Amount</th>
                            <th>BXA</th>
                            <th>Purpose</th>
                          </tr>
                        </thead>
                        <tbody>
                          {unmatched.map((row) => (
                            <tr key={row.operationId}>
                              <td>
                                <Input
                                  type="checkbox"
                                  className="position-static m-0"
                                  checked={importPicks.includes(row.operationId)}
                                  onChange={() =>
                                    togglePick(
                                      importPicks,
                                      setImportPicks,
                                      row.operationId,
                                    )
                                  }
                                />
                              </td>
                              <td>{row.operationDate}</td>
                              <td className="text-truncate" style={{ maxWidth: 200 }}>
                                {row.candidateName}
                              </td>
                              <td>{row.pinpp}</td>
                              <td>{row.examDate}</td>
                              <td>{money(row.amount)}</td>
                              <td>
                                <code>{row.contractNumber}</code>
                              </td>
                              <td
                                className="text-truncate text-muted small"
                                style={{ maxWidth: 260 }}
                                title={row.purpose}
                              >
                                {row.purpose}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </>
                )}

                {ambiguous.length > 0 && (
                  <>
                    <h5 className="mt-4">
                      Needs a person to decide{" "}
                      <Badge color="danger" className="text-white">
                        {ambiguous.length}
                      </Badge>
                    </h5>
                    <p className="text-muted small mb-2">
                      More than one IELTS payment has this pinpp, exam date and
                      amount, so none of them is changed automatically.
                    </p>
                    <div style={{ maxHeight: 300, overflow: "auto" }}>
                      <Table striped size="sm" className="mb-0">
                        <thead>
                          <tr>
                            <th>Paid</th>
                            <th>Candidate</th>
                            <th>PINPP</th>
                            <th>Exam date</th>
                            <th>Amount</th>
                            <th>BXA</th>
                            <th>Matching payments</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ambiguous.map((row) => (
                            <tr key={row.operationId}>
                              <td>{row.operationDate}</td>
                              <td className="text-truncate" style={{ maxWidth: 200 }}>
                                {row.candidateName}
                              </td>
                              <td>{row.pinpp}</td>
                              <td>{row.examDate}</td>
                              <td>{money(row.amount)}</td>
                              <td>
                                <code>{row.contractNumber}</code>
                              </td>
                              <td className="small">
                                {row.payments
                                  .map((one) => one.enquiryId || one.id)
                                  .join(", ")}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </>
                )}

                <h6 className="mt-4">Left out of this fetch</h6>
                <Table bordered size="sm" style={{ maxWidth: 520 }}>
                  <tbody>
                    {SKIP_REASONS.filter(([key]) => counts[key]).map(
                      ([key, label]) => (
                        <tr key={key}>
                          <td className="text-muted">{label}</td>
                          <td style={{ width: 90 }}>{counts[key]}</td>
                        </tr>
                      ),
                    )}
                  </tbody>
                </Table>
              </>
            )}
          </CardBody>
        </Card>
      </div>
    </AppLayout>
  );
};

export default SmartupSync;
